import { useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Sliders, Plus, RefreshCw, Search, Pencil, Trash2, X,
  CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight,
  ShieldCheck, Save, Filter,
} from 'lucide-react';

import { policyApi, POLICY_TYPE_LABELS, POLICY_TYPE_COLORS } from '@/api/policy.api';
import type { PolicyListDto, CreatePolicyRequest } from '@/api/policy.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';
import { RoleLookup, UserLookup } from '@/components/lookups/EntityLookups';

// ── Constants ─────────────────────────────────────────────────────────────────

const POLICY_TYPES = [
  'Access', 'ApprovalLimit', 'Posting', 'Override',
  'ReportVisibility', 'Workflow', 'Password', 'Session',
] as const;

const PAGE_SIZE = 20;

// ── Schema ────────────────────────────────────────────────────────────────────

const policySchema = z.object({
  policyCode:       z.string().min(1, 'Required').max(50),
  policyName:       z.string().min(1, 'Required').max(200),
  policyType:       z.string().min(1, 'Required'),
  appliesToRoleId:  z.string().optional().nullable(),
  appliesToUserId:  z.string().optional().nullable(),
  locationId:       z.string().optional().nullable(),
  costCenterId:     z.string().optional().nullable(),
  transactionType:  z.string().max(100).optional().nullable(),
  permissionCode:   z.string().max(100).optional().nullable(),
  amountLimitFrom:  z.coerce.number().optional().nullable(),
  amountLimitTo:    z.coerce.number().optional().nullable(),
  currencyCode:     z.string().max(3).optional().nullable(),
  effectiveFrom:    z.string().optional().nullable(),
  effectiveTo:      z.string().optional().nullable(),
  isActive:         z.boolean().default(true),
  remarks:          z.string().max(500).optional().nullable(),
});
type PolicyFormValues = z.infer<typeof policySchema>;

// ── Helpers ───────────────────────────────────────────────────────────────────

const inputCls = (disabled?: boolean) =>
  cn(
    'w-full px-3 py-2 text-sm border rounded-lg transition-colors',
    'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700',
    'text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500',
    disabled && 'bg-slate-50 dark:bg-slate-800 cursor-not-allowed opacity-70',
  );

function extractApiError(err: unknown): string {
  const e = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
  const d = e?.response?.data;
  if (d?.errors) return Object.values(d.errors).flat().join(' ');
  return d?.message ?? 'An unexpected error occurred.';
}

// ── Policy Type Badge ─────────────────────────────────────────────────────────

const PolicyTypeBadge = ({ type }: { type: string }) => (
  <span className={cn(
    'px-2 py-0.5 rounded-full text-xs font-medium',
    POLICY_TYPE_COLORS[type] ?? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-gray-400',
  )}>
    {POLICY_TYPE_LABELS[type] ?? type}
  </span>
);

// ── Policy Form Modal ─────────────────────────────────────────────────────────

interface PolicyFormModalProps {
  editId:    string | null;
  onClose:   () => void;
  onSaved:   () => void;
}

const PolicyFormModal = ({ editId, onClose, onSaved }: PolicyFormModalProps) => {
  const [loading, setLoading] = useState(!!editId);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const form = useForm<PolicyFormValues>({
    resolver: zodResolver(policySchema),
    defaultValues: {
      policyCode: '', policyName: '', policyType: 'Access',
      isActive: true,
    },
  });

  useEffect(() => {
    if (!editId) return;
    setLoading(true);
    policyApi.getById(editId)
      .then(res => {
        const p = res.data?.data;
        if (p) form.reset({
          policyCode:      p.policyCode,
          policyName:      p.policyName,
          policyType:      p.policyType,
          appliesToRoleId: p.appliesToRoleId,
          appliesToUserId: p.appliesToUserId,
          locationId:      p.locationId,
          costCenterId:    p.costCenterId,
          transactionType: p.transactionType,
          permissionCode:  p.permissionCode,
          amountLimitFrom: p.amountLimitFrom,
          amountLimitTo:   p.amountLimitTo,
          currencyCode:    p.currencyCode,
          effectiveFrom:   p.effectiveFrom?.slice(0, 10),
          effectiveTo:     p.effectiveTo?.slice(0, 10),
          isActive:        p.isActive,
          remarks:         p.remarks,
        });
      })
      .catch(() => setError('Failed to load policy.'))
      .finally(() => setLoading(false));
  }, [editId, form]);

  const handleSubmit = async (values: PolicyFormValues) => {
    setSaving(true);
    setError('');
    try {
      const payload: CreatePolicyRequest = {
        ...values,
        amountLimitFrom: values.amountLimitFrom ?? null,
        amountLimitTo:   values.amountLimitTo   ?? null,
        effectiveFrom:   values.effectiveFrom   ? `${values.effectiveFrom}T00:00:00Z` : null,
        effectiveTo:     values.effectiveTo     ? `${values.effectiveTo}T23:59:59Z`   : null,
      };
      if (editId) {
        await policyApi.update(editId, payload);
      } else {
        await policyApi.create(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const selectedType = form.watch('policyType');
  const isApproval = selectedType === 'ApprovalLimit';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700
                      w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div className="flex items-center gap-2">
            <Sliders size={16} className="text-blue-600" />
            <span className="font-semibold text-slate-900 dark:text-white text-sm">
              {editId ? 'Edit Policy' : 'New Policy'}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-10"><Spinner size="lg" /></div>
        ) : (
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-auto p-5 space-y-4">

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 text-xs">
                  <AlertTriangle size={13} className="shrink-0" />
                  {error}
                </div>
              )}

              {/* Core identity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                    Policy Code <span className="text-red-500">*</span>
                  </label>
                  <input {...form.register('policyCode')} className={cn(inputCls(), 'font-mono uppercase')}
                    placeholder="e.g. APPR-JV-MGR" />
                  {form.formState.errors.policyCode && (
                    <p className="mt-1 text-xs text-red-500">{form.formState.errors.policyCode.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                    Policy Type <span className="text-red-500">*</span>
                  </label>
                  <select {...form.register('policyType')} className={inputCls()}>
                    {POLICY_TYPES.map(t => (
                      <option key={t} value={t}>{POLICY_TYPE_LABELS[t] ?? t}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                    Policy Name <span className="text-red-500">*</span>
                  </label>
                  <input {...form.register('policyName')} className={inputCls()} />
                  {form.formState.errors.policyName && (
                    <p className="mt-1 text-xs text-red-500">{form.formState.errors.policyName.message}</p>
                  )}
                </div>
              </div>

              {/* Applies to */}
              <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500 mb-3">Applies To</p>
                <div className="grid grid-cols-2 gap-4">
                  <Controller name="appliesToRoleId" control={form.control}
                    render={({ field }) => (
                      <RoleLookup label="Role" value={field.value} onChange={field.onChange}
                        placeholder="All roles" hint="Leave blank to apply to every role." />
                    )}
                  />
                  <Controller name="appliesToUserId" control={form.control}
                    render={({ field }) => (
                      <UserLookup label="User" value={field.value} onChange={field.onChange}
                        placeholder="All users" hint="Leave blank to apply to every user." />
                    )}
                  />
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                      Transaction Type
                    </label>
                    <input {...form.register('transactionType')} className={inputCls()}
                      placeholder="e.g. GL_JOURNAL, AP_BILL" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                      Permission Code
                    </label>
                    <input {...form.register('permissionCode')} className={cn(inputCls(), 'font-mono text-xs')}
                      placeholder="e.g. APPROVE_GL_JOURNAL" />
                  </div>
                </div>
              </div>

              {/* Approval Limits — only show for ApprovalLimit type */}
              {isApproval && (
                <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500 mb-3">Approval Amount Limits</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                        Minimum Amount
                      </label>
                      <input {...form.register('amountLimitFrom')} type="number" step="0.01"
                        className={inputCls()} placeholder="0.00" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                        Maximum Amount
                      </label>
                      <input {...form.register('amountLimitTo')} type="number" step="0.01"
                        className={inputCls()} placeholder="No limit" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                        Currency
                      </label>
                      <input {...form.register('currencyCode')} className={cn(inputCls(), 'uppercase font-mono')}
                        maxLength={3} placeholder="USD" />
                    </div>
                  </div>
                </div>
              )}

              {/* Effective dates + status */}
              <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500 mb-3">Validity</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                      Effective From
                    </label>
                    <input {...form.register('effectiveFrom')} type="date" className={inputCls()} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                      Effective To
                    </label>
                    <input {...form.register('effectiveTo')} type="date" className={inputCls()} />
                  </div>
                  <div className="flex items-center pt-5">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input type="checkbox" {...form.register('isActive')}
                        className="rounded border-slate-300 text-blue-600 w-4 h-4" />
                      <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">Active</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Remarks</label>
                <textarea {...form.register('remarks')} rows={2} maxLength={500}
                  className={cn(inputCls(), 'resize-none')}
                  placeholder="Optional notes about this policy…" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-5 border-t border-slate-200 dark:border-slate-700 shrink-0">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600
                           text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-blue-600 text-white
                           rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {saving ? <Spinner size="sm" /> : <Save size={14} />}
                {editId ? 'Save Changes' : 'Create Policy'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ── Confirm Delete Dialog ─────────────────────────────────────────────────────

interface ConfirmDeleteProps {
  policy:   PolicyListDto;
  onClose:  () => void;
  onDeleted: () => void;
}
const ConfirmDelete = ({ policy, onClose, onDeleted }: ConfirmDeleteProps) => {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await policyApi.delete(policy.id);
      onDeleted();
      onClose();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Trash2 size={18} className="text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="font-semibold text-slate-900 dark:text-white text-sm">Delete Policy</p>
            <p className="text-xs text-slate-500">This action cannot be undone.</p>
          </div>
        </div>
        <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
          Delete policy <span className="font-mono font-semibold">{policy.policyCode}</span> — {policy.policyName}?
        </p>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
            Cancel
          </button>
          <button onClick={handleDelete} disabled={deleting}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg
                       bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors">
            {deleting ? <Spinner size="sm" /> : <Trash2 size={13} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export const PolicySetupPage = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.ADMIN_POLICY_CREATE);
  const canEdit   = hasPermission(Permissions.ADMIN_POLICY_EDIT);
  const canDelete = hasPermission(Permissions.ADMIN_POLICY_DELETE);

  const [items,    setItems]    = useState<PolicyListDto[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [editId,         setEditId]         = useState<string | null>(null);
  const [showForm,       setShowForm]       = useState(false);
  const [deleteTarget,   setDeleteTarget]   = useState<PolicyListDto | null>(null);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async (p: number, s: string, t: string, a: 'all' | 'active' | 'inactive') => {
    setLoading(true);
    try {
      const res = await policyApi.getList({
        pageNumber: p,
        pageSize:   PAGE_SIZE,
        searchTerm: s || undefined,
        policyType: t || undefined,
        isActive:   a === 'all' ? undefined : a === 'active',
      });
      const data = res.data?.data;
      if (data) { setItems(data.items); setTotal(data.totalCount); }
    } catch {
      showToast('Failed to load policies.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, search, typeFilter, activeFilter); }, [page, load, search, typeFilter, activeFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load(1, search, typeFilter, activeFilter);
  };

  const handleSaved = () => {
    showToast(editId ? 'Policy updated.' : 'Policy created.');
    load(page, search, typeFilter, activeFilter);
  };

  const handleDeleted = () => {
    showToast('Policy deleted.');
    load(page, search, typeFilter, activeFilter);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-xl text-sm font-medium flex items-center gap-2',
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <PolicyFormModal
          editId={editId}
          onClose={() => { setShowForm(false); setEditId(null); }}
          onSaved={handleSaved}
        />
      )}
      {deleteTarget && (
        <ConfirmDelete
          policy={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
        />
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sliders size={20} className="text-blue-600" />
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Policy Setup</h1>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total > 0 ? `${total.toLocaleString()} polic${total !== 1 ? 'ies' : 'y'}` : 'No policies configured'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(page, search, typeFilter, activeFilter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh">
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => { setEditId(null); setShowForm(true); }}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium
                           rounded-lg hover:bg-blue-700 transition-colors">
                <Plus size={16} /> New Policy
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search code or name…"
                className="pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg
                           bg-white dark:bg-slate-700 text-slate-900 dark:text-white w-56
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button type="submit"
              className="px-3 py-2 text-sm font-medium bg-slate-100 dark:bg-slate-700
                         text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 transition-colors">
              Search
            </button>
          </form>

          {/* Type filter */}
          <div className="flex items-center gap-1">
            <Filter size={13} className="text-slate-400" />
            <select
              value={typeFilter}
              onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
              className="text-xs border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1.5
                         bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none"
            >
              <option value="">All Types</option>
              {Object.entries(POLICY_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {/* Active filter */}
          <div className="flex items-center gap-1">
            {(['all', 'active', 'inactive'] as const).map(f => (
              <button key={f}
                onClick={() => { setActiveFilter(f); setPage(1); }}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-colors capitalize',
                  activeFilter === f
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200',
                )}>
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <ShieldCheck size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-medium text-slate-500 dark:text-gray-400">No policies found</p>
            <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">
              {search || typeFilter ? 'Try adjusting your filters.' : 'Create your first policy to get started.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Policy Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Applies To</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Limits</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Validity</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(p => (
                <tr key={p.id} className="hover:bg-blue-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-300 font-medium">
                    {p.policyCode}
                  </td>
                  <td className="px-4 py-3 text-slate-900 dark:text-white text-sm font-medium max-w-xs">
                    <p className="truncate">{p.policyName}</p>
                    {p.transactionType && (
                      <p className="text-xs text-slate-400 dark:text-gray-500 font-mono">{p.transactionType}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <PolicyTypeBadge type={p.policyType} />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400">
                    {p.appliesToRoleName && <p>Role: {p.appliesToRoleName}</p>}
                    {p.appliesToUserName && <p>User: {p.appliesToUserName}</p>}
                    {!p.appliesToRoleName && !p.appliesToUserName && (
                      <span className="text-slate-400 dark:text-gray-500 italic">All</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400">
                    {p.amountLimitFrom != null || p.amountLimitTo != null ? (
                      <span className="font-mono">
                        {p.amountLimitFrom?.toLocaleString() ?? '0'} — {p.amountLimitTo?.toLocaleString() ?? '∞'}
                        {p.currencyCode && ` ${p.currencyCode}`}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                    {p.effectiveFrom ? new Date(p.effectiveFrom).toLocaleDateString('en-GB') : '—'}
                    {' → '}
                    {p.effectiveTo   ? new Date(p.effectiveTo).toLocaleDateString('en-GB')   : '∞'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      p.isActive
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-slate-100 text-slate-500 dark:text-gray-400 dark:bg-slate-700',
                    )}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      {canEdit && (
                        <button
                          onClick={() => { setEditId(p.id); setShowForm(true); }}
                          className="p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          title="Edit">
                          <Pencil size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => setDeleteTarget(p)}
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ──────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700
                        bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">
            Page {page} of {totalPages} · {total.toLocaleString()} policies
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button key={n} onClick={() => setPage(n)}
                  className={cn('w-8 h-8 text-sm rounded transition-colors',
                    n === page ? 'bg-blue-600 text-white font-semibold'
                              : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300')}>
                  {n}
                </button>
              );
            })}
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
